import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import debounce from "lodash.debounce";
import { fetchPractitioner, fetchPractitionerRole } from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";
import { Link, Navigate, useSearchParams } from "react-router";
import Select from 'react-select';

const limit = 20;

const searchOptions = [
    { value: 'practitionerHccId', label: 'Practitioner HccId' },
    { value: 'taxId', label: 'Tax ID' },
    { value: 'practitionerNpi', label: 'Practitioner NPI' },
    { value: 'practitionerKey', label: 'Practitioner Key' }
]

const practitionerHccOption = searchOptions[0];

const withQuery = (path, params) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== "") qs.set(k, String(v));
    });
    const s = qs.toString();
    return s ? `${path}?${s}` : path;
};

const Practitioners = () => {
    const [searchParams] = useSearchParams();
    const hasNetwork = searchParams.get("benefitNetworkId") || searchParams.get("supplierNetworkHccId");
    if (hasNetwork && !searchParams.get("practitionerHccId")) {
        return <Navigate to={`/providers/roles?${searchParams.toString()}`} replace />;
    }
    return <PractitionersCatalog />;
};

const PractitionersCatalog = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlHccId = searchParams.get("practitionerHccId") || "";
    const benefitNetworkId = searchParams.get("benefitNetworkId") || "";
    const supplierNetworkHccId = searchParams.get("supplierNetworkHccId") || "";
    const networkName = searchParams.get("networkName") || "";
    const networkFilter = benefitNetworkId || supplierNetworkHccId;

    const [searchValue, setSearchValue] = useState(urlHccId || undefined);
    const [decouncedSearchValue, setDecouncedSearchValue] = useState(urlHccId || undefined);

    const [searchBy, setSearchBy] = useState(urlHccId ? practitionerHccOption : null);

    const [showMoreColumns, setShowMoreColumns] = useState(false);

    const { ref, inView } = useInView();

    const { data, error, fetchNextPage, hasNextPage, isLoading, isFetching, isFetchingNextPage, status } = useInfiniteQuery({
        queryKey: ["practitioner", limit, decouncedSearchValue, searchBy?.value],
        queryFn: async ({ pageParam }) => {
            let params = pageParam || {};

            if (decouncedSearchValue && searchBy?.value) {
                params[searchBy?.value] = decouncedSearchValue;
            }

            return await fetchPractitioner({ ...params, limit });
        },

        select: (data) => {
            return { count: data.pages?.[0]?.count, rows: data.pages.map((page) => page.rows).flatMap((x) => x) };
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            if (!lastPage?.rows?.length || lastPage.rows.length < limit) return undefined;
            return {
                offset: pages.reduce((accum, page) => accum + page.rows.length, 0),
            };
        },
        staleTime: 0,
        enabled: true
    });

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchValue(event.target.value?.trim());
        }, 500),
        []
    );

    let columns = [
        'practitionerHccId',
        'taxId',
        'practitionerNpi',
        'practitionerFirstName',
        'practitionerLastName',
        'isPcp',
        'practitionerStatus',
        'practEffectiveDateKey',
        'practTerminationDateKey',
        "versionEffDateKey",
        "versionExpDateKey"
    ];

    if (showMoreColumns) {
        columns = [
            'practitionerKey',
            'primarySpecialtyKey',
            ...columns,
        ];
    }

    const onDetailHandler = async (body) => {
        const roles = await fetchPractitionerRole({
            includeTerminated: 'true',
            practitionerHccId: body.practitionerHccId,
        })

        return roles
    }

    const toRoles = (row) => row.practitionerHccId
        ? withQuery("/providers/roles", {
            practitionerHccId: row.practitionerHccId,
            benefitNetworkId,
            supplierNetworkHccId,
            networkName,
        })
        : null;

    const columnLinks = {
        practitionerHccId: toRoles,
    };

    return (
        <div className={'container '}>
            <p className='title'>Practitioners ( {data?.count || 0} )</p>
            <p className="sub-title">
                Practitioners from HealthRules. Open Detail for roles at suppliers. Or go to{" "}
                <Link to="/providers/roles">Roles</Link> to search roles directly.
            </p>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1rem 1rem 0rem', }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%', flexWrap: 'wrap', }}>
                    {networkFilter && (
                        <Select
                            name="network"
                            className="react-select-container large-input"
                            classNamePrefix="react-select"
                            placeholder="Network"
                            isClearable
                            value={{
                                value: networkFilter,
                                label: `${benefitNetworkId ? "Benefit network" : "Supplier network"}: ${networkName || networkFilter}`,
                            }}
                            options={[{
                                value: networkFilter,
                                label: `${benefitNetworkId ? "Benefit network" : "Supplier network"}: ${networkName || networkFilter}`,
                            }]}
                            onChange={(option) => {
                                if (!option) {
                                    const next = new URLSearchParams(searchParams);
                                    next.delete("benefitNetworkId");
                                    next.delete("supplierNetworkHccId");
                                    next.delete("networkName");
                                    setSearchParams(next);
                                }
                            }}
                        />
                    )}
                    <Select
                        name={'searchBy'}
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        placeholder={'Search by'}
                        options={searchOptions}
                        value={searchBy}
                        onChange={(option) => { setSearchBy(option) }}
                    />

                    {searchBy && <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={"search"}
                        placeholder="search..."
                        autoComplete="off"
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                            debouncedChangeHandler(e);
                        }}
                        value={searchValue}
                    />}

                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={() => {
                        setSearchBy(null)
                        setDecouncedSearchValue("");
                        setSearchValue("");
                        if (urlHccId || networkFilter) setSearchParams({});
                    }}
                >
                    Clear filters
                </p>

                <p style={{ cursor: "pointer", color: "var(--green)" }} onClick={() => setShowMoreColumns(!showMoreColumns)}>
                    {showMoreColumns ? "Less Columns" : "More Columns"}
                </p>

            </div>


            {status === "loading" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className='danger'>Error: {error.message}</span>
            ) : (
                <>
                    <SharedTable
                        type={"practitioners"}
                        columns={columns}
                        items={data?.rows}
                        activeDraftId={null}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        fetchNextPage={fetchNextPage}
                        isFetching={isFetching}
                        isLoading={isLoading}
                        onDetailHandler={onDetailHandler}
                        columnLinks={columnLinks}
                        ref={ref}
                    />
                </>
            )}

        </div>
    )
}

export default Practitioners
